import React, { useState, useRef, useEffect } from 'react';
import { ArrowUpIcon, Loader2 } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent } from './components/ui/card';
import { Textarea } from './components/ui/textarea';
import AvatarFallback from './components/AvatarFallback';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL
  || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5001/api');

// Anthropic calls occasionally have transient latency spikes or drop mid-request;
// one silent retry recovers from those without surfacing an error to the user.
const postWithRetry = async (url, data, retries = 1) => {
  try {
    return await axios.post(url, data);
  } catch (error) {
    if (retries > 0) {
      await new Promise((resolve) => setTimeout(resolve, 800));
      return postWithRetry(url, data, retries - 1);
    }
    throw error;
  }
};

function App() {
  const [messages, setMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [messageAnswers, setMessageAnswers] = useState({});
  const [lockedMessages, setLockedMessages] = useState(new Set());
  const [loadingMessages, setLoadingMessages] = useState(new Set());
  const [isMultiline, setIsMultiline] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const MAX_TEXTAREA_HEIGHT = 160; // ~7 lines
  const SINGLE_LINE_HEIGHT = 44;

  const handleInputChange = (e) => {
    setCurrentInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT);
    el.style.height = next + 'px';
    setIsMultiline(next > SINGLE_LINE_HEIGHT + 4);
  };


  const handleSendMessage = async () => {
    if (!currentInput.trim() || isLoading || isSending) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: currentInput.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentInput('');
    setIsMultiline(false);
    if (textareaRef.current) {
      textareaRef.current.style.height = '44px';
    }
    setIsSending(true);
    setIsLoading(true);
    
    // Reset isSending after a brief moment (user action is complete)
    setTimeout(() => setIsSending(false), 100);

    try {
      // Use the new combined API endpoint with model configuration
      const response = await postWithRetry(`${API_BASE_URL}/check-and-respond`, {
        prompt: userMessage.content
      });

      if (response.data.needsClarification) {
        const clarifyingMessage = {
          id: Date.now() + 1,
          type: 'assistant-clarifying',
          content: response.data.message,
          questions: response.data.questions,
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, clarifyingMessage]);
      } else {
        const answerMessage = {
          id: Date.now() + 1,
          type: 'assistant-answer',
          content: response.data.answer,
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, answerMessage]);
      }
    } catch (error) {
      console.error('Error processing message:', error);
      
      // Show error message to user
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant-answer',
        content: "I'm sorry, I encountered an error processing your request. Please try again.",
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsSending(false);
    }
  };

  const handleAnswerChange = (messageId, questionIndex, value) => {
    setMessageAnswers(prev => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        [questionIndex]: value
      }
    }));
  };

  const handleSubmitAnswers = async (messageId) => {
    const currentAnswers = messageAnswers[messageId] || {};
    // Check if all fields are empty (treat as skip)
    const hasAnswers = Object.values(currentAnswers).some(answer => answer && answer.trim() !== '');
    
    if (!hasAnswers) {
      // Treat as skip if no answers provided
      handleSkipQuestions(messageId);
      return;
    }

    // Lock the message and set loading state for this specific message
    setLockedMessages(prev => new Set([...prev, messageId]));
    setLoadingMessages(prev => new Set([...prev, messageId]));
    
    try {
      // Find the clarifying message to get its index
      const clarifyingMessageIndex = messages.findIndex(msg => msg.id === messageId);
      if (clarifyingMessageIndex === -1) {
        throw new Error('Clarifying message not found');
      }
      
      // Find the user message that comes before this clarifying message
      const userMessage = messages.slice(0, clarifyingMessageIndex)
        .reverse()
        .find(msg => msg.type === 'user');
      
      if (!userMessage) {
        throw new Error('Original user message not found');
      }

      // Generate answer using the new API endpoint with clarifications
      const answerResponse = await postWithRetry(`${API_BASE_URL}/generate-final`, {
        prompt: userMessage.content,
        clarifications: Object.values(currentAnswers).filter(answer => answer && answer.trim() !== ''),
        skipClarifications: false,
        fromClarificationFlow: true
      });
      
      const answerMessage = {
        id: Date.now(),
        type: 'assistant-answer',
        content: answerResponse.data.answer,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, answerMessage]);
    } catch (error) {
      console.error('Error generating answer:', error);
      
      // Show error message to user
      const errorMessage = {
        id: Date.now(),
        type: 'assistant-answer',
        content: "I'm sorry, I encountered an error generating your personalized answer. Please try again.",
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoadingMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
      setIsSending(false);
    }
  };

  const handleSkipQuestions = async (messageId) => {
    // Lock the message and set loading state for this specific message
    setLockedMessages(prev => new Set([...prev, messageId]));
    setLoadingMessages(prev => new Set([...prev, messageId]));
    
    try {
      // Find the clarifying message to get its index
      const clarifyingMessageIndex = messages.findIndex(msg => msg.id === messageId);
      if (clarifyingMessageIndex === -1) {
        throw new Error('Clarifying message not found');
      }
      
      // Find the user message that comes before this clarifying message
      const userMessage = messages.slice(0, clarifyingMessageIndex)
        .reverse()
        .find(msg => msg.type === 'user');
      
      if (!userMessage) {
        throw new Error('Original user message not found');
      }

      // Generate answer using the new API endpoint without clarifications
      const answerResponse = await postWithRetry(`${API_BASE_URL}/generate-final`, {
        prompt: userMessage.content,
        skipClarifications: true,
        fromClarificationFlow: true
      });
      
      const answerMessage = {
        id: Date.now(),
        type: 'assistant-answer',
        content: answerResponse.data.answer,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, answerMessage]);
    } catch (error) {
      console.error('Error generating answer:', error);
      
      // Show error message to user
      const errorMessage = {
        id: Date.now(),
        type: 'assistant-answer',
        content: "I'm sorry, I encountered an error generating your answer. Please try again.",
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoadingMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderMessage = (message) => {
    if (message.type === 'user') {
      return (
        <div key={message.id} className="flex justify-end mb-4 animate-message-in">
          <div className="flex items-start space-x-2 max-w-[80%]">
            <div className="bg-blue-500 text-white rounded-lg px-4 py-2 shadow-sm">
              <p className="text-sm text-pretty">{message.content}</p>
            </div>
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
              <AvatarFallback size={32}>User</AvatarFallback>
            </div>
          </div>
        </div>
      );
    }

    if (message.type === 'assistant-clarifying') {
      const isLocked = lockedMessages.has(message.id);
      const currentAnswers = messageAnswers[message.id] || {};
      const answeredCount = Object.values(currentAnswers).filter(answer => answer && answer.trim() !== '').length;
      const totalQuestions = message.questions.length;
      
      return (
        <div key={message.id} className="flex justify-start mb-4 animate-message-in">
          <div className="flex items-start space-x-2 max-w-[80%]">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
              <AvatarFallback size={32}>Assistant</AvatarFallback>
            </div>
            <Card className={`border border-gray-200 dark:border-slate-700 ${isLocked ? 'bg-gray-100 dark:bg-slate-700' : 'bg-white dark:bg-slate-800'}`}>
              <CardContent className="p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 text-pretty">{message.content}</p>

                <div className={`space-y-3 rounded-xl p-3 ${isLocked ? 'bg-gray-200/50 dark:bg-slate-900/30' : 'bg-gray-50 dark:bg-slate-900/40'}`}>
                  {message.questions.map((question, index) => (
                    <div
                      key={index}
                      className="space-y-1 animate-message-in"
                      style={{ animationDelay: `${index * 80}ms` }}
                    >
                      <label className={`text-sm font-medium ${isLocked ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                        {index + 1}. {question}
                      </label>
                      <Textarea
                        value={currentAnswers[index] || ''}
                        onChange={(e) => handleAnswerChange(message.id, index, e.target.value)}
                        placeholder="Your answer"
                        className={`min-h-[40px] resize-none text-sm ${
                          isLocked
                            ? 'bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                            : 'bg-white dark:bg-slate-800'
                        }`}
                        disabled={isLocked}
                      />
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 mt-4">
                  {isLocked ? (
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      {answeredCount > 0 ? (
                        <span className="tabular-nums">✓ {answeredCount}/{totalQuestions} answered</span>
                      ) : (
                        <span>✓ Skipped</span>
                      )}
                    </div>
                  ) : (
                    <>
                      <Button
                        onClick={() => handleSubmitAnswers(message.id)}
                        disabled={loadingMessages.has(message.id)}
                        size="sm"
                      >
                        <span
                          className={`relative inline-block h-4 overflow-hidden transition-[width,margin] duration-300 ease-icon-swap ${
                            loadingMessages.has(message.id) ? 'w-4 mr-2' : 'w-0 mr-0'
                          }`}
                        >
                          <Loader2
                            className={`absolute inset-0 h-4 w-4 animate-spin transition-opacity duration-300 ease-icon-swap ${
                              loadingMessages.has(message.id) ? 'opacity-100' : 'opacity-0'
                            }`}
                          />
                        </span>
                        Add Context
                      </Button>

                      <Button
                        onClick={() => handleSkipQuestions(message.id)}
                        disabled={loadingMessages.has(message.id)}
                        variant="outline"
                        size="sm"
                      >
                        Skip & Continue
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    if (message.type === 'assistant-answer') {
      return (
        <div key={message.id} className="flex justify-start mb-4 animate-message-in">
          <div className="flex items-start space-x-2 max-w-[80%]">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
              <AvatarFallback size={32}>Assistant</AvatarFallback>
            </div>
            <Card className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
              <CardContent className="p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-pretty">{message.content}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-[#1A3041] flex items-center justify-center p-4" style={{ minHeight: '100vh' }}>
      {/* Full viewport background to prevent white space on scroll */}
      <div className="fixed inset-0 bg-[#1A3041] -z-10"></div>
      
      <div className="w-full max-w-4xl relative h-[85vh]">
        {/* Soft light glow to lift the card off the dark background */}
        <div className="absolute inset-0 bg-white/5 rounded-2xl blur-lg scale-[1.02]"></div>

        {/* Chat Container */}
                <Card className="relative h-full flex flex-col shadow-2xl border-0 bg-white dark:bg-slate-900 rounded-2xl">
            {/* Messages Area - Scrollable Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400 mt-8">
                  <div className="w-12 h-12 mx-auto mb-4">
                    <AvatarFallback size={48}>Assistant</AvatarFallback>
                  </div>
                  <p className="text-pretty">Drop your question. I'll help you think it through.</p>
                </div>
              ) : (
                messages.map(renderMessage)
              )}
              
              {isLoading && (
                <div className="flex justify-start mb-4 animate-message-in">
                  <div className="flex items-start space-x-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <AvatarFallback size={32}>Assistant</AvatarFallback>
                    </div>
                    <Card className="bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
                      <CardContent className="p-4">
                        <div className="flex items-center space-x-2">
                          <Loader2 className="w-4 h-4 animate-spin text-gray-500 dark:text-gray-400" />
                          <span className="text-sm text-gray-500 dark:text-gray-400">Thinking...</span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area - Fixed Footer with Fade Effect */}
            <div className="relative p-4 flex-shrink-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-b-2xl">
              {/* Fade effect overlay */}
              <div className="absolute inset-x-0 -top-8 h-8 bg-gradient-to-b from-transparent to-white dark:to-slate-900 pointer-events-none"></div>
              <div className="relative">
              <Textarea
                ref={textareaRef}
                value={currentInput}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Start typing..."
                rows={1}
                className={`w-full min-h-0 resize-none py-3 pl-4 pr-14 text-sm leading-5 transition-[border-radius] duration-200 ease-icon-swap ${
                  isMultiline ? 'rounded-2xl overflow-y-auto' : 'rounded-full overflow-y-hidden'
                }`}
                style={{ height: '44px' }}
                disabled={isLoading}
              />
                <Button
                  variant="default"
                  size="icon"
                  aria-label="Submit"
                  onClick={handleSendMessage}
                  disabled={!currentInput.trim() || isSending}
                  className="absolute bottom-1.5 right-1.5 h-8 w-8 rounded-full before:content-[''] before:absolute before:-inset-1"
                >
                  <span className="relative block h-4 w-4">
                    <ArrowUpIcon
                      className={`absolute inset-0 h-4 w-4 transition-[opacity,transform,filter] duration-300 ease-icon-swap ${
                        isSending ? 'opacity-0 scale-25 blur-sm' : 'opacity-100 scale-100 blur-none'
                      }`}
                    />
                    <Loader2
                      className={`absolute inset-0 h-4 w-4 animate-spin transition-[opacity,transform,filter] duration-300 ease-icon-swap ${
                        isSending ? 'opacity-100 scale-100 blur-none' : 'opacity-0 scale-25 blur-sm'
                      }`}
                    />
                  </span>
                </Button>
              </div>
            </div>
          </Card>
        </div>
    </div>
  );
}

export default App;